import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import '../../assets/styles/infrastructure_leasing_modal.scss';
import {
  dispatchInfrastructureNotificationsRefresh,
  rejectInfrastructureListingDeal,
} from '../../services/infrastructureLeasingListing.service';

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

export default function InfrastructureLeasingDealRejectModal({
  open,
  onClose,
  listingId,
  enquiryId,
  listingTitle,
  buyerName,
  onSuccess,
  title = 'Reject deal',
  submitLabel = 'Reject enquiry',
}) {
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const suffix = `${listingId || 'x'}-${enquiryId || 'y'}`;

  useEffect(() => {
    if (open) {
      setReason('');
      setComments('');
      setSubmitting(false);
    }
  }, [open, listingId, enquiryId]);

  if (!open) return null;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const lid = Number(listingId);
    const eid = Number(enquiryId);
    if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
      toast.error('Missing listing or enquiry.');
      return;
    }
    const r = str(reason);
    if (r.length < 3) {
      toast.warning('Please enter a reason for rejection (at least 3 characters).');
      return;
    }

    setSubmitting(true);
    try {
      const res = await rejectInfrastructureListingDeal(lid, eid, {
        reason: r,
        comments: str(comments) || null,
      });
      if (res?.success) {
        toast.success(res.message || 'Enquiry rejected.');
        dispatchInfrastructureNotificationsRefresh();
        onSuccess?.(res.data);
        onClose();
      } else {
        toast.error(res?.message || 'Could not reject.');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message;
      toast.error(msg || 'Could not reject enquiry.');
    } finally {
      setSubmitting(false);
    }
  };

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={() => !submitting && onClose()} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`ile-reject-title-${suffix}`}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button
              type="button"
              className="ile-modal-panel__close"
              onClick={() => !submitting && onClose()}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`ile-reject-title-${suffix}`}>
              {str(title) || 'Reject deal'}
            </h2>
            <p className="ile-modal-panel__subtitle">
              {str(listingTitle) || 'Listing'} · {str(buyerName) || 'Buyer'}
            </p>
          </div>

          <form className="ile-modal-panel__form" onSubmit={handleSubmit} noValidate>
            <div className="ile-modal-panel__body">
              <div className="ile-modal-block">
                <label className="ile-form-label" htmlFor={`ile-rej-reason-${suffix}`}>
                  Reason for rejection <span className="text-danger">*</span>
                </label>
                <textarea
                  id={`ile-rej-reason-${suffix}`}
                  className="input-pill"
                  style={{ minHeight: 88, resize: 'vertical' }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  minLength={3}
                  maxLength={512}
                />
                <label className="ile-form-label mt-2" htmlFor={`ile-rej-comments-${suffix}`}>
                  Additional comments (optional)
                </label>
                <textarea
                  id={`ile-rej-comments-${suffix}`}
                  className="input-pill"
                  style={{ minHeight: 72, resize: 'vertical' }}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  maxLength={4000}
                />
              </div>
            </div>

            <div className="ile-modal-panel__footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-danger btn-sm" disabled={submitting}>
                {submitting ? 'Rejecting…' : str(submitLabel) || 'Reject enquiry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalUi, document.body);
  }
  return modalUi;
}
